import axios from 'axios';
import TokenService from './service.token';
import { NAVIGATION_ROUTES } from '../routes/constants';

const THREE_MINUTES = 3 * 60 * 1000;

// Dynamically set baseURL based on domain
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
let baseURL = 'https://api.kurtasari.com/';
// let baseURL = 'http://192.168.1.81:8000/';

if (hostname.includes('kurtasari.com')) {
  baseURL = 'https://api.kurtasari.com/';
} else if (hostname.includes('kurtasaree.com')) {
  baseURL = 'https://api.kurtasaree.com/';
}

const AxiosInstance = axios.create({
  baseURL,
  timeout: THREE_MINUTES,
});

// Utility function to check if a token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const expiryTime = tokenPayload.exp * 1000;
    const currentTime = Date.now();
    const refreshThreshold = 5 * 1000;
    return expiryTime - currentTime < refreshThreshold;
  } catch (error) {
    console.error('Error parsing token:', error);
    return true; // Treat invalid token as expired
  }
};

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const notifyRefreshSubscribers = (newToken: string) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

const refreshAccessToken = async (): Promise<string | null> => {
  if (isRefreshing) {
    return new Promise((resolve) => {
      refreshSubscribers.push(resolve);
    });
  }

  isRefreshing = true;

  try {
    const tokenData = TokenService.getToken();
    if (!tokenData?.refresh) throw new Error('No refresh token available.');

    const response = await axios.post(`${baseURL}api/v1/accounts/token/refresh/`, {
      refresh: tokenData.refresh,
    });

    const newAccessToken = response.data.access;
    TokenService.setToken({ ...tokenData, access: newAccessToken });
    notifyRefreshSubscribers(newAccessToken);
    return newAccessToken;
  } catch {
    handleLogout();
    return null;
  } finally {
    isRefreshing = false;
  }
};

// Function to handle logout and redirection
const handleLogout = () => {
  TokenService.clearToken();
  // Clear localStorage and sessionStorage
  localStorage.clear();
  sessionStorage.clear();
  // Redirect to sign-in page
  if (typeof window !== 'undefined') {
    window.location.href = NAVIGATION_ROUTES.SIGNIN;
  }
};

AxiosInstance.interceptors.request.use(
  async (config) => {
    const tokenData = TokenService.getToken();
    const accessToken = tokenData?.access;

    if (accessToken) {
      if (isTokenExpired(accessToken)) {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          config.headers.Authorization = `Bearer ${newAccessToken}`;
        }
      } else {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

AxiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Skip redirect logic if the current route is the sign-in page
    const isSignInPage =
      typeof window !== 'undefined' && window.location.pathname === NAVIGATION_ROUTES.SIGNIN;

    // Handle only 401 (Unauthorized) errors, but not on the sign-in page
    if (error.response?.status === 401 && !isSignInPage) {
      handleLogout();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

let isRefreshStarted = false;

const startTokenRefresh = () => {
  if (isRefreshStarted) return;
  isRefreshStarted = true;

  const refreshInterval = 55 * 60 * 1000;

  setInterval(async () => {
    const tokenData = TokenService.getToken();
    if (tokenData?.access && isTokenExpired(tokenData.access)) {
      await refreshAccessToken();
    }
  }, refreshInterval);
};

startTokenRefresh();

export { AxiosInstance };
